import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'
import type {
  SubscriptionPlanResponse,
  ActiveSubscriptionResponse,
  SubscriptionStatusResponse,
  CancelSubscriptionResponse,
  CustomerPortalResponse,
  InvoiceListResponse,
} from '@/data/types/subscription.types'

// API Functions

async function listSubscriptionPlans(): Promise<SubscriptionPlanResponse[]> {
  return fetchWithAuth('/api/v1/subscription-plans')
}

async function getSubscriptionPlanById(subscriptionPlanId: string): Promise<SubscriptionPlanResponse> {
  return fetchWithAuth(`/api/v1/subscription-plans/${encodeURIComponent(subscriptionPlanId)}`)
}

async function getActiveSubscription(): Promise<ActiveSubscriptionResponse> {
  return fetchWithAuth('/api/v1/subscriptions/active')
}

async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  return fetchWithAuth('/api/v1/subscriptions/status')
}

async function cancelSubscription(immediate: boolean = false): Promise<CancelSubscriptionResponse> {
  return fetchWithAuth(`/api/v1/subscriptions/cancel?immediate=${immediate}`, {
    method: 'POST',
  })
}

async function getCustomerPortal(): Promise<CustomerPortalResponse> {
  return fetchWithAuth('/api/v1/subscriptions/portal')
}

interface GetInvoicesParams {
  limit?: number
  offset?: number
}

async function getInvoices(params: GetInvoicesParams = {}): Promise<InvoiceListResponse> {
  const { limit = 10, offset = 0 } = params
  return fetchWithAuth(`/api/v1/subscriptions/invoices?limit=${limit}&offset=${offset}`)
}

// React Query Hooks

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscriptions', 'plans'],
    queryFn: listSubscriptionPlans,
  })
}

export function useSubscriptionPlan(subscriptionPlanId: string | null) {
  return useQuery({
    queryKey: ['subscriptions', 'plans', subscriptionPlanId],
    queryFn: () => getSubscriptionPlanById(subscriptionPlanId!),
    enabled: !!subscriptionPlanId,
  })
}

export function useActiveSubscription() {
  return useQuery({
    queryKey: ['subscriptions', 'active'],
    queryFn: getActiveSubscription,
  })
}

/**
 * Hook for polling subscription status after checkout
 * Use with refetchInterval to poll until is_active becomes true
 */
export function useSubscriptionStatus(options?: {
  enabled?: boolean
  refetchInterval?: number | false
}) {
  return useQuery({
    queryKey: ['subscriptions', 'status'],
    queryFn: getSubscriptionStatus,
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  })
}

/**
 * Hook for canceling the current subscription
 * @param immediate - If true, cancel immediately. If false (default), cancel at period end.
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (immediate: boolean = false) => cancelSubscription(immediate),
    onSuccess: () => {
      // Invalidate subscription queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
    },
  })
}

/**
 * Hook for getting the Paddle customer portal URL
 */
export function useCustomerPortal() {
  return useQuery({
    queryKey: ['subscriptions', 'portal'],
    queryFn: getCustomerPortal,
    // Don't auto-fetch, only fetch when explicitly needed
    enabled: false,
  })
}

/**
 * Hook for fetching invoice/billing history with server-side pagination
 */
export function useInvoices(params: { limit?: number; offset?: number } = {}) {
  const { limit = 10, offset = 0 } = params
  return useQuery({
    queryKey: ['subscriptions', 'invoices', { limit, offset }],
    queryFn: () => getInvoices({ limit, offset }),
  })
}